import { Form, Input, Typography, Button } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { GdModal } from '../../../components/GdModal';
import { GlossaryWord } from '../stores/glossaryStore';

const ACCEPTED_IMAGE_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.tiff,.tif,.avif';

const ImagePickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ImagePreview = styled.img`
  max-height: 80px;
  max-width: 120px;
  object-fit: contain;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 2px;
`;

const FileName = styled(Typography.Text)`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
`;

type Props = {
  isVisible: boolean;
  isLoading: boolean;
  editingWord?: GlossaryWord | null;
  onClose: () => void;
  onSubmit: (
    values: Omit<GlossaryWord, 'id' | 'tenantId' | 'checked'>,
    imageFile?: File | null
  ) => void;
};

export const GlossaryWordModal = ({
  isVisible,
  isLoading,
  editingWord,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const isEdit = !!editingWord;

  useEffect(() => {
    if (isVisible) {
      form.setFieldsValue(
        editingWord
          ? {
              word: editingWord.word,
              text: editingWord.text,
              link: editingWord.link,
              labelLink: editingWord.labelLink,
            }
          : { word: '', text: '', link: '', labelLink: '' }
      );
      setSelectedFile(null);
      setPreviewUrl(editingWord?.imageUrl || null);
    }
  }, [isVisible, editingWord, form]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setSelectedFile(file);
      // Only show preview for raster formats; SVG is safe too
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    },
    []
  );

  const handlePickerClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSubmit(
        {
          word: values.word || '',
          text: values.text || '',
          link: values.link || '',
          labelLink: values.labelLink || '',
          imageUrl: editingWord?.imageUrl || '',
        },
        selectedFile
      );
    } catch {
      // validation failed — form shows inline errors
    }
  }, [form, onSubmit, editingWord, selectedFile]);

  return (
    <GdModal
      isVisible={isVisible}
      isClosable={!isLoading}
      disableButtons={isLoading}
      loading={isLoading}
      onCloseModal={onClose}
      onOkHandler={handleOk}
      title={
        isEdit
          ? t('glossary:AddModal:editTitle')
          : t('glossary:AddModal:addTitle')
      }
      okButtonText={
        isEdit
          ? t('glossary:AddModal:saveBtnLbl')
          : t('glossary:AddModal:addBtnLbl')
      }
      cancelButtonText={t('glossary:AddModal:cancelBtnLbl')}
      width={520}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="word"
          label={t('glossary:AddModal:wordLbl')}
          rules={[{ required: true, message: t('glossary:AddModal:wordLbl') }]}
        >
          <Input
            className="gd-input-item"
            placeholder={t('glossary:AddModal:wordPlaceholder')}
          />
        </Form.Item>
        <Form.Item name="text" label={t('glossary:AddModal:textLbl')}>
          <Input.TextArea
            className="gd-input-item"
            placeholder={t('glossary:AddModal:textPlaceholder')}
            rows={3}
          />
        </Form.Item>
        <Form.Item name="link" label={t('glossary:AddModal:linkLbl')}>
          <Input
            className="gd-input-item"
            placeholder={t('glossary:AddModal:linkPlaceholder')}
          />
        </Form.Item>
        <Form.Item name="labelLink" label={t('glossary:AddModal:labelLinkLbl')}>
          <Input
            className="gd-input-item"
            placeholder={t('glossary:AddModal:labelLinkPlaceholder')}
          />
        </Form.Item>
        <Form.Item label={t('glossary:AddModal:imageUrlLbl')}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <ImagePickerRow>
            <Button onClick={handlePickerClick} disabled={isLoading}>
              {t('glossary:AddModal:chooseImageBtn')}
            </Button>
            {selectedFile ? (
              <FileName>{selectedFile.name}</FileName>
            ) : previewUrl && !selectedFile ? (
              <FileName>
                {editingWord?.imageUrl
                  ? editingWord.imageUrl.split('/').pop()
                  : ''}
              </FileName>
            ) : (
              <FileName type="secondary">
                {t('glossary:AddModal:noImageChosen')}
              </FileName>
            )}
          </ImagePickerRow>
          {previewUrl && (
            <div style={{ marginTop: 8 }}>
              <ImagePreview src={previewUrl} alt="preview" />
            </div>
          )}
        </Form.Item>
      </Form>
    </GdModal>
  );
};
